import routes from '@config/routes';
import { serverFetcher } from '@instance';
import { addQueryParam } from '@utils/add-query-param';
import { getCanonicalLeagueTeamSlug } from '@utils/navigation/league-public-paths';
import { type EventResponse } from '@v2/types';
import { getConcertImagePath } from './config';
import { guardLocationEventsForTeam } from './guard-team-events';
import { formatLeagueSlug, getVenueImages } from './getVenueImages';
import { WORLD_CUP_TEAM_SLUG_MAP } from './constants/worldCupTeamSlugs';
import {
  getEuropeanSoccerTeamByCategory,
  isRelevantUefaEventName,
} from './getTeamsByLeague/europeanSoccer';

type EventsByLocationResponse = {
  events: EventResponse[];
  totalEvents: number;
};

const UEFA_CATEGORY_NAMES = new Set([
  'uefa-soccer',
  'champions-league',
  'champions-league-final',
  'europa-league',
  'europa-league-final',
  'conference-league',
  'conference-league-final',
]);

export interface Props {
  page?: number;
  perPage?: number;
  lat: number;
  lon: number;
  serverSide?: boolean;
  categoryName?: string;
  withVenueImages?: boolean;
  startDate?: string;
  endDate?: string;
  teamSlug?: string;
  onlyHomeGames?: boolean;
  opponentId?: number;
  concerts?: boolean;
  sports?: boolean;
  league?: string;
  rejectInvalidResponse?: boolean;
}

export const getEventsByLocation = async ({
  lat,
  lon,
  page = 1,
  perPage = 50,
  concerts = false,
  sports = false,
  withVenueImages = false,
  startDate,
  endDate,
  teamSlug,
  categoryName,
  league,
  opponentId,
  serverSide = false,
  rejectInvalidResponse = false,
}: Props): Promise<EventsByLocationResponse> => {
  const rawSlug = teamSlug || categoryName;
  const resolvedSlug =
    teamSlug && league === 'world-cup'
      ? (WORLD_CUP_TEAM_SLUG_MAP[teamSlug.toLowerCase()]?.slug ?? rawSlug)
      : teamSlug && (league || categoryName)
        ? getCanonicalLeagueTeamSlug(league || categoryName || '', teamSlug)
        : rawSlug;
  const res = await serverFetcher(
    `${routes.eventLocation}?${addQueryParam({ page, perPage, lat, lon, concerts, sports, startDate, endDate, teamSlug: resolvedSlug, league: league, opponentId })}`,
    {
      method: 'GET',
      ...(serverSide
        ? { next: { revalidate: 3600 } }
        : { cache: 'no-store' as RequestCache }),
    }
  );

  if (rejectInvalidResponse && !Array.isArray(res?.events)) {
    throw new Error('Unable to load location events');
  }

  // add this temporary so it won't cause breaking changes on staging
  const europeanTeam = teamSlug
    ? getEuropeanSoccerTeamByCategory(league ?? categoryName ?? '', teamSlug)
    : undefined;
  const eventNameIncludes = europeanTeam?.eventNameIncludes?.toLowerCase();
  const filteredEvents = (Array.isArray(res?.events) ? res.events : []).filter(
    (event: EventResponse) =>
      (europeanTeam?.teamId
        ? event.performances?.some(
            ({ performer }) => performer.id === europeanTeam.teamId
          )
        : true) &&
      (!eventNameIncludes ||
        event.name?.toLowerCase().includes(eventNameIncludes)) &&
      (!UEFA_CATEGORY_NAMES.has(league ?? '') ||
        isRelevantUefaEventName(event.name))
  );
  const shouldFilterEvents = Boolean(europeanTeam?.teamId || eventNameIncludes);
  const filteredResponse = shouldFilterEvents
    ? { ...res, events: filteredEvents, totalEvents: filteredEvents.length }
    : res;

  // Guard against category-leak payloads when a teamSlug is requested.
  const teamGuardedResponse = guardLocationEventsForTeam(
    filteredResponse,
    teamSlug ? resolvedSlug : undefined,
    europeanTeam?.teamId
  );

  const eventsList = Array.isArray(teamGuardedResponse?.events)
    ? teamGuardedResponse.events
    : [];
  const totalEvents =
    teamGuardedResponse?.totalEvents ??
    teamGuardedResponse?.total_entries ??
    (teamGuardedResponse as { total?: number })?.total ??
    eventsList.length;

  if (!withVenueImages) {
    return { events: eventsList, totalEvents };
  }

  if (concerts) {
    const formattedEvents = eventsList.map((event) => {
      return {
        ...event,
        venue: {
          ...event?.venue,
          image: getConcertImagePath(event.id ?? event.name),
        },
      };
    });

    return {
      events: formattedEvents,
      totalEvents,
    };
  }

  const venueImages = await getVenueImages(eventsList);
  const venueImageByKey = new Map(
    venueImages.map((item) => [
      `${item.venueId}:${item.league}`,
      item.imagePath,
    ])
  );

  const formattedEvents = eventsList.map((event) => {
    const venueImage = venueImageByKey.get(
      `${event?.venue?.id}:${formatLeagueSlug(event?.category?.slug)}`
    );
    return {
      ...event,
      venue: { ...event?.venue, image: venueImage },
    };
  });

  return {
    events: formattedEvents,
    totalEvents,
  };
};
